import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useUser } from "../lib/userAuth";
import { Star, Image as ImageIcon, Loader2, X } from "lucide-react";

export default function ReviewsSection({ slug, waterfallName, embedded = false }) {
  const { user, signIn } = useUser();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/waterfalls/${slug}/reviews`);
      setItems(r.data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  return (
    <section className={embedded ? "" : "py-10 border-t border-stone-200"} data-testid="reviews-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-stone-900">Reviews</h2>
        {user ? (
          <button onClick={() => setShowForm((v) => !v)} className="btn-at btn-at-green text-sm" data-testid="write-review-btn">
            {showForm ? "Cancel" : "Write a review"}
          </button>
        ) : (
          <button onClick={() => signIn(null, `Sign in to leave a review and share your experience at ${waterfallName || "this waterfall"}.`)} className="btn-at btn-at-dark text-sm" data-testid="signin-to-review-btn">
            Sign in to review
          </button>
        )}
      </div>

      {showForm && user && (
        <ReviewForm slug={slug} onDone={() => { setShowForm(false); load(); }} />
      )}

      {loading ? (
        <p className="text-stone-500">Loading reviews…</p>
      ) : items.length === 0 ? (
        <p className="text-stone-500 italic">No reviews yet. Be the first to share your experience at {waterfallName}.</p>
      ) : (
        <div className="space-y-8" data-testid="reviews-list">
          {items.map((r) => (
            <article key={r.id} className="flex gap-4">
              {r.user_picture ? (
                <img src={r.user_picture} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" referrerPolicy="no-referrer" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center text-stone-700 font-semibold flex-shrink-0">
                  {(r.user_name || "?")[0]?.toUpperCase()}
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-stone-900">{r.user_name}</span>
                  <span className="text-stone-300">·</span>
                  <span className="text-sm text-stone-500">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-0.5 mb-2">
                  {[1,2,3,4,5].map(n => <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-stone-900 text-stone-900" : "text-stone-300"}`} />)}
                </div>
                <p className="text-stone-700 leading-relaxed whitespace-pre-line">{r.text}</p>
                {r.image_urls?.length > 0 && (
                  <div className="mt-3 flex gap-2 flex-wrap">
                    {r.image_urls.map((u, i) => (
                      <img key={i} src={u + (u.includes("?") ? "&" : "?") + "w=400&q=70"} alt="" className="w-32 h-24 object-cover rounded-lg" />
                    ))}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function ReviewForm({ slug, onDone }) {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    setMsg("");
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/me/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, r.data.url]);
      }
    } catch (e) {
      setMsg(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) { setMsg("Please write a review"); return; }
    setBusy(true);
    setMsg("");
    try {
      await api.post(`/waterfalls/${slug}/reviews`, { rating, text, image_urls: images });
      setMsg("Thanks! Your review is awaiting approval.");
      setTimeout(onDone, 1500);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Submit failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-stone-200 p-6 mb-8 bg-stone-50" data-testid="review-form">
      <p className="text-sm font-semibold text-stone-700 mb-2">Your rating</p>
      <div className="flex items-center gap-1 mb-4">
        {[1,2,3,4,5].map(n => (
          <button key={n} type="button" onClick={() => setRating(n)} aria-label={`${n} stars`} data-testid={`rating-${n}`}>
            <Star className={`w-7 h-7 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-stone-300"}`} />
          </button>
        ))}
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Share your experience — best time to visit, what to bring, anything other travellers should know…"
        rows={5}
        maxLength={4000}
        data-testid="review-text"
        className="w-full rounded-lg border border-stone-300 px-3 py-2 outline-none focus:ring-2 focus:ring-stone-900"
      />
      <div className="mt-3 flex items-center flex-wrap gap-2">
        {images.map((u, i) => (
          <div key={u + i} className="relative">
            <img src={u + "?w=160&q=70"} alt="" className="w-20 h-20 object-cover rounded-lg" />
            <button type="button" onClick={() => setImages(images.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 bg-white rounded-full shadow p-1" aria-label="Remove">
              <X className="w-3 h-3 text-stone-700" />
            </button>
          </div>
        ))}
        {images.length < 6 && (
          <label className="w-20 h-20 rounded-lg border-2 border-dashed border-stone-300 hover:border-stone-900 flex flex-col items-center justify-center text-stone-500 cursor-pointer">
            <ImageIcon className="w-5 h-5" />
            <span className="text-[10px] mt-1">Add photo</span>
            <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} data-testid="review-upload" />
          </label>
        )}
      </div>
      {msg && <p className="mt-3 text-sm text-stone-700">{msg}</p>}
      <div className="mt-4 flex items-center gap-3">
        <button type="submit" disabled={busy} className="btn-at btn-at-green disabled:opacity-50" data-testid="submit-review-btn">
          {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          {busy ? "Submitting…" : "Submit review"}
        </button>
        <p className="text-xs text-stone-500">Reviews are checked before going live.</p>
      </div>
    </form>
  );
}
